import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { Header } from '../../components/Header/Header';
import { Triage } from '../../components/Triage/Triage';
import { SortableClientCard } from '../../components/ClientCard/SortableClientCard';
import { IdleClientCard } from '../../components/ClientCard/IdleClientCard';
import { ClientDrawer } from '../../components/ClientDrawer/ClientDrawer';
import { TaskDrawer } from '../../components/TaskDrawer/TaskDrawer';
import { ClientEditor } from '../../components/ClientEditor/ClientEditor';
import { AssignModal } from '../../components/AssignModal/AssignModal';
import { LineupDrawer } from '../../components/LineupDrawer/LineupDrawer';
import { Toast } from '../../components/Toast/Toast';
import { BoltIcon } from '../../components/Icon';
import { useStudioData } from '../../hooks/useStudioData';
import { useToast } from '../../hooks/useToast';
import { useGoogleConnection } from '../../hooks/useGoogleConnection';
import { useSlackConnections } from '../../hooks/useSlackConnections';
import { activeTasks, lineupItems, totalOpen } from '../../lib/selectors';
import { handToCursor } from '../../lib/cursor';
import {
  aiEnabled,
  connectGoogleAccount,
  connectSlackWorkspace,
  disconnectGoogleAccount,
  disconnectSlackWorkspace,
  scanInboxes,
  setSlackWorkspaceClient,
  suggestTriageAssignment,
} from '../../lib/ai';
import { requestGmailAuthCode } from '../../lib/google';
import { requestSlackAuthCode } from '../../lib/slack';
import type { AssignmentSuggestion } from '../../lib/ai';
import type { ClientWithTasks, TriageItem } from '../../types/models';
import styles from './StudioBoard.module.css';

/** Auto-assign when Claude is at least this confident; otherwise confirm. */
const AUTO_ASSIGN_THRESHOLD = 0.5;

/** Target minimum card width and inter-column gap (matches the CSS). */
const MIN_COL_WIDTH = 360;
const COL_GAP = 20;

/** Distribute clients round-robin so reading order stays left-to-right. */
function toColumns(clients: ClientWithTasks[], cols: number): ClientWithTasks[][] {
  const columns: ClientWithTasks[][] = Array.from({ length: cols }, () => []);
  clients.forEach((client, i) => columns[i % cols].push(client));
  return columns;
}

export function StudioBoard() {
  const data = useStudioData();
  const { message, flash } = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [editingClientId, setEditingClientId] = useState<string | null>(null);
  const [lineupOpen, setLineupOpen] = useState(false);
  // Id of the triage item Claude is currently routing (inline spinner).
  const [pendingId, setPendingId] = useState<string | null>(null);
  // True while Claude scans connected inboxes for actionable messages.
  const [scanning, setScanning] = useState(false);
  // True while the one-time "Connect Gmail" consent popup is in flight.
  const [connectingGmail, setConnectingGmail] = useState(false);
  const gmailConnected = useGoogleConnection();
  // True while a "Connect a Slack workspace" popup is in flight.
  const [connectingSlack, setConnectingSlack] = useState(false);
  const slackConnections = useSlackConnections();
  // Low-confidence fallback: confirm/adjust in a modal.
  const [manual, setManual] = useState<{
    item: TriageItem;
    suggestion: AssignmentSuggestion | null;
  } | null>(null);

  const gridRef = useRef<HTMLDivElement>(null);
  const [colCount, setColCount] = useState(1);

  // Compute how many columns fit the available width (responsive masonry).
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const compute = () => {
      const width = el.clientWidth;
      setColCount(Math.max(1, Math.floor((width + COL_GAP) / (MIN_COL_WIDTH + COL_GAP))));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [data.loading]);

  // Clients with open work stay in the mosaic; the rest park in the bottom rail.
  const { activeClients, idleClients } = useMemo(() => {
    const active: ClientWithTasks[] = [];
    const idle: ClientWithTasks[] = [];
    for (const client of data.clients) {
      (activeTasks(client).length > 0 ? active : idle).push(client);
    }
    return { activeClients: active, idleClients: idle };
  }, [data.clients]);

  const columns = useMemo(
    () => toColumns(activeClients, colCount),
    [activeClients, colCount],
  );

  const lineup = useMemo(() => lineupItems(data.clients), [data.clients]);
  const lineupOpenCount = lineup.filter((e) => !e.task.done).length;

  const selected = data.clients.find((c) => c.id === selectedId) ?? null;
  const editingClient = data.clients.find((c) => c.id === editingClientId) ?? null;

  // Resolve the selected task and its owning client across all clients.
  const selectedTask = selectedTaskId
    ? data.clients.flatMap((c) => c.tasks).find((t) => t.id === selectedTaskId) ?? null
    : null;
  const selectedTaskClient = selectedTask
    ? data.clients.find((c) => c.id === selectedTask.clientId) ?? null
    : null;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = activeClients.map((c) => c.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    // Keep idle clients appended so their relative order is preserved.
    const reordered = arrayMove(ids, from, to).concat(idleClients.map((c) => c.id));
    data.reorderClients(reordered);
  };

  const onCursor = (title: string) => {
    void handToCursor(title);
    flash('Handed to Cursor — prompt copied');
  };

  const onToggleTask = (taskId: string) => {
    data.toggleTask(taskId);
  };

  const onAddTask = (clientId: string, title: string) => {
    if (!title.trim()) return;
    data.addTask(clientId, title);
    flash('Task added');
  };

  const onCapture = (title: string) => {
    if (!title.trim()) return;
    data.captureTriage(title);
    flash('Captured to triage');
  };

  const onDismissTriage = (itemId: string) => {
    data.dismissTriage(itemId);
    flash('Dismissed');
  };

  const onAssign = async (itemId: string) => {
    const item = data.triage.find((t) => t.id === itemId);
    if (!item) return;

    // Pre-assigned by sender-domain match — confirm against that client directly.
    if (item.suggestedClientId) {
      setManual({
        item,
        suggestion: {
          clientId: item.suggestedClientId,
          clientName: item.suggestedClientName ?? '',
          projectId: '',
          projectName: '',
          taskTitle: item.title,
          confidence: 1,
          reasoning: 'Sender domain matches this client.',
        },
      });
      return;
    }

    // No AI available (seed mode / unconfigured) — go straight to manual.
    if (!aiEnabled) {
      setManual({ item, suggestion: null });
      return;
    }

    setPendingId(itemId);
    try {
      const s = await suggestTriageAssignment({
        triageId: item.id,
        title: item.title,
        source: item.source,
      });
      if (s.clientId && s.confidence >= AUTO_ASSIGN_THRESHOLD) {
        data.assignTriageToClient(item.id, s.clientId, s.projectId, s.taskTitle);
        flash(`Assigned to ${s.clientName || 'client'} · ${Math.round(s.confidence * 100)}%`);
      } else {
        // Unsure — let the user confirm or adjust.
        setManual({ item, suggestion: s });
      }
    } catch {
      setManual({ item, suggestion: null });
      flash('Claude unavailable — assign manually');
    } finally {
      setPendingId(null);
    }
  };

  const onScanInboxes = async () => {
    setScanning(true);
    try {
      const r = await scanInboxes();
      const parts: string[] = [];
      if (r.added > 0) parts.push(`${r.added} to triage`);
      if (r.attachments > 0) {
        parts.push(`${r.attachments} attachment${r.attachments === 1 ? '' : 's'}`);
      }
      if (parts.length > 0) {
        flash(parts.join(' · '));
      } else if (r.scanned > 0) {
        flash('No new actionable emails');
      } else {
        flash('Nothing new to scan');
      }
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const onConnectGmail = async () => {
    setConnectingGmail(true);
    try {
      const { code, redirectUri } = await requestGmailAuthCode();
      await connectGoogleAccount(code, redirectUri);
      flash('Gmail connected');
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Could not connect Gmail');
    } finally {
      setConnectingGmail(false);
    }
  };

  const onDisconnectGmail = async () => {
    try {
      await disconnectGoogleAccount();
      flash('Gmail disconnected');
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Could not disconnect Gmail');
    }
  };

  const onConnectSlack = async () => {
    setConnectingSlack(true);
    try {
      const { code, redirectUri } = await requestSlackAuthCode();
      const { teamName } = await connectSlackWorkspace(code, redirectUri);
      flash(`Connected Slack workspace "${teamName}"`);
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Could not connect Slack workspace');
    } finally {
      setConnectingSlack(false);
    }
  };

  const onDisconnectSlack = async (teamId: string) => {
    try {
      await disconnectSlackWorkspace(teamId);
      flash('Slack workspace disconnected');
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Could not disconnect Slack workspace');
    }
  };

  const onSetSlackWorkspaceClient = async (teamId: string, clientId: string | null) => {
    try {
      await setSlackWorkspaceClient(teamId, clientId);
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Could not update workspace client tag');
    }
  };

  return (
    <div className={styles.page}>
      <div className="grain" />
      <Header
        totalOpen={totalOpen(data.clients)}
        clientCount={data.clients.length}
        onCapture={onCapture}
      />

      <main className={styles.main}>
        <Triage
          items={data.triage}
          onAssign={onAssign}
          onCursor={onCursor}
          onDismiss={onDismissTriage}
          pendingId={pendingId}
          onScan={aiEnabled ? onScanInboxes : undefined}
          scanning={scanning}
          gmailConnected={gmailConnected}
          onConnectGmail={onConnectGmail}
          connectingGmail={connectingGmail}
          onDisconnectGmail={onDisconnectGmail}
          slackConnections={slackConnections}
          onConnectSlack={onConnectSlack}
          connectingSlack={connectingSlack}
          onDisconnectSlack={onDisconnectSlack}
          onSetSlackWorkspaceClient={onSetSlackWorkspaceClient}
          clients={data.clients.map((c) => ({ id: c.id, name: c.name }))}
        />

        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Clients</h2>
          <span className={styles.sectionHint}>
            Drag to reorder · click for the full board
          </span>
          <button
            type="button"
            className={`${styles.lineupLaunch} ${lineupOpenCount > 0 ? styles.lineupLaunchActive : ''}`}
            onClick={() => setLineupOpen(true)}
          >
            <BoltIcon size={14} />
            Lineup
            {lineup.length > 0 && <span className={styles.lineupCount}>{lineupOpenCount}</span>}
          </button>
        </div>

        {data.loading ? (
          <div className={styles.loading}>Loading the board…</div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={activeClients.map((c) => c.id)}
              strategy={rectSortingStrategy}
            >
              <div className={styles.mosaic} ref={gridRef}>
                {columns.map((col, i) => (
                  <div className={styles.col} key={i}>
                    {col.map((client) => (
                      <SortableClientCard
                        key={client.id}
                        client={client}
                        onOpen={() => setSelectedId(client.id)}
                        onEdit={() => setEditingClientId(client.id)}
                        onToggleTask={onToggleTask}
                        onAddTask={(title) => onAddTask(client.id, title)}
                        onCursor={onCursor}
                        onSelectTask={setSelectedTaskId}
                        onToggleLineup={data.toggleLineup}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {!data.loading && idleClients.length > 0 && (
          <section className={styles.idleSection}>
            <div className={styles.idleHead}>
              <h2 className={styles.idleTitle}>No open tasks</h2>
              <span className={styles.idleHint}>
                {idleClients.length} client{idleClients.length === 1 ? '' : 's'} idle · add a
                task to bring one back up
              </span>
            </div>
            <div className={`${styles.idleRail} sb-scroll`}>
              {idleClients.map((client) => (
                <IdleClientCard
                  key={client.id}
                  client={client}
                  onOpen={() => setSelectedId(client.id)}
                  onAddTask={(title) => onAddTask(client.id, title)}
                />
              ))}
            </div>
          </section>
        )}

        <div className={styles.footer}>
          Mithril Media · Internal · Stay Curious. And Evolve.
        </div>
      </main>

      {selected && (
        <ClientDrawer
          client={selected}
          onClose={() => setSelectedId(null)}
          onToggleTask={onToggleTask}
          onAddTask={(title) => onAddTask(selected.id, title)}
          onCursor={onCursor}
          onSelectTask={setSelectedTaskId}
          onToggleLineup={data.toggleLineup}
          onEdit={() => setEditingClientId(selected.id)}
        />
      )}

      {selectedTask && selectedTaskClient && (
        <TaskDrawer
          key={selectedTask.id}
          task={selectedTask}
          client={selectedTaskClient}
          allClients={data.clients}
          onClose={() => setSelectedTaskId(null)}
          onUpdate={(patch) => {
            data.updateTask(selectedTask.id, patch);
            if (patch.clientId && patch.clientId !== selectedTask.clientId) {
              const name = data.clients.find((c) => c.id === patch.clientId)?.name ?? 'client';
              flash(`Moved to ${name}`);
            }
          }}
          onToggle={() => onToggleTask(selectedTask.id)}
          onDelete={() => {
            data.deleteTask(selectedTask.id);
            setSelectedTaskId(null);
            flash('Task deleted');
          }}
          onCursor={onCursor}
          onToggleLineup={() => data.toggleLineup(selectedTask.id)}
          onUploadAttachment={(file) => data.uploadTaskAttachment(selectedTask.id, file)}
        />
      )}

      {manual && (
        <AssignModal
          item={manual.item}
          clients={data.clients}
          suggestion={manual.suggestion}
          onClose={() => setManual(null)}
          onConfirm={(clientId, projectId, title) => {
            data.assignTriageToClient(manual.item.id, clientId, projectId, title);
            const name = data.clients.find((c) => c.id === clientId)?.name ?? 'client';
            setManual(null);
            flash(`Assigned to ${name}`);
          }}
        />
      )}

      {lineupOpen && (
        <LineupDrawer
          entries={lineup}
          onClose={() => setLineupOpen(false)}
          onToggleTask={onToggleTask}
          onSelectTask={(taskId) => {
            setSelectedTaskId(taskId);
            setLineupOpen(false);
          }}
          onToggleLineup={data.toggleLineup}
          onCursor={onCursor}
          onClear={() => {
            data.clearLineup();
            flash('Lineup cleared');
          }}
          onReorder={data.reorderLineup}
        />
      )}

      {editingClient && (
        <ClientEditor
          client={editingClient}
          onClose={() => setEditingClientId(null)}
          onSave={(patch) => {
            data.updateClient(editingClient.id, patch);
            flash('Client updated');
          }}
          onUploadLogo={async (file) => {
            try {
              await data.uploadClientLogo(editingClient.id, file);
              flash('Logo uploaded');
            } catch (e) {
              flash(e instanceof Error ? `Upload failed: ${e.message}` : 'Upload failed');
              throw e;
            }
          }}
        />
      )}

      <Toast message={message} />
    </div>
  );
}
